/**
 * Delete Rock Dialog
 *
 * Confirmation dialog for archiving (soft deleting) a rock.
 * Shows warning about child rocks that will be unlinked.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { ShortCodeBadge } from "./short-code-badge";
import { useDeleteRock } from "@/hooks/use-rocks-mutations";

interface DeleteRockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rock: {
    id: string;
    short_code: string;
    title: string;
    owner: string;
    status: string;
  } | null;
  childRockCount?: number;
  onDeleted?: () => void;
}

export function DeleteRockDialog({
  open,
  onOpenChange,
  rock,
  childRockCount = 0,
  onDeleted,
}: DeleteRockDialogProps) {
  const { mutateAsync: deleteRock, isPending } = useDeleteRock();

  if (!rock) return null;

  const isDraft = rock.status === "draft";

  const handleDelete = async () => {
    try {
      await deleteRock({ rockId: rock.id });
      onOpenChange(false);
      onDeleted?.();
    } catch {
      // Error toast is handled by mutation hook
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Rock
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to delete this rock? This action will archive the rock
                and it will no longer appear in the active rock list.
              </p>

              <div className="bg-muted rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <ShortCodeBadge shortCode={rock.short_code} />
                  <span className="font-medium text-foreground">{rock.owner}</span>
                </div>
                <p className="text-sm text-foreground">{rock.title}</p>
              </div>

              {childRockCount > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-yellow-700 dark:text-yellow-400">
                  <p className="font-medium">Warning: This rock has {childRockCount} child rock{childRockCount > 1 ? "s" : ""}</p>
                  <p className="text-sm mt-1">
                    Deleting this rock will unlink all child rocks. They will become standalone rocks
                    without a parent.
                  </p>
                </div>
              )}

              {!isDraft && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-orange-700 dark:text-orange-400">
                  <p className="text-sm">
                    <span className="font-medium">Note:</span> This rock has status "{rock.status.replace("_", " ")}".
                    Only admins can delete non-draft rocks.
                  </p>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Rock"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
